/* Pipeline 172X: application entry point
   The public Pipeline story lives at https://172x.ai/pipeline.
   This host is the product application, so unauthenticated visitors get a
   focused entry screen instead of a second copy of the marketing site. */

import { useState } from "react";

import { ALPHA_ACCESS_ENABLED } from "./config";

const PRODUCT = "172X | Pipeline";
const COMPANY = "Mastylo Labs LLC";

const externalLinks = {
  marketing: "https://172x.ai/pipeline",
  ecosystem: "https://172x.ai/",
};

function ActionButton({
  children,
  onClick,
  variant = "primary",
}: {
  children: React.ReactNode;
  onClick: () => void;
  variant?: "primary" | "secondary";
}) {
  const primary = variant === "primary";
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        alignItems: "center",
        background: primary ? "#1a3a5c" : "transparent",
        border: primary ? "1px solid #1a3a5c" : "1px solid #cbd5e1",
        borderRadius: 8,
        color: primary ? "#fff" : "#1f2937",
        cursor: "pointer",
        display: "inline-flex",
        fontFamily: '"DM Sans", sans-serif',
        fontSize: 14,
        fontWeight: 650,
        justifyContent: "center",
        minHeight: 46,
        padding: "0 22px",
        transition: "background 150ms ease, border-color 150ms ease",
      }}
    >
      {children}
      <span aria-hidden="true" style={{ fontSize: 17, marginLeft: 9 }}>
        →
      </span>
    </button>
  );
}

function PipelineHeader({
  onRequestAlpha,
  onSignIn,
}: {
  onRequestAlpha: () => void;
  onSignIn: () => void;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header
      style={{
        background: "rgba(255,255,255,0.94)",
        backdropFilter: "blur(12px)",
        borderBottom: "1px solid #e5e7eb",
        position: "sticky",
        top: 0,
        zIndex: 20,
      }}
    >
      <div
        style={{
          alignItems: "center",
          display: "flex",
          height: 66,
          justifyContent: "space-between",
          margin: "0 auto",
          maxWidth: 1120,
          padding: "0 24px",
        }}
      >
        <button
          type="button"
          onClick={() => window.location.assign(externalLinks.ecosystem)}
          className="inline-flex items-baseline whitespace-nowrap font-display font-wordmark text-xl text-ink tracking-tight hover:text-cobalt transition-colors duration-150"
          aria-label="172X home"
        >
          <span className="font-extrabold">172X</span>
        </button>

        <div style={{ alignItems: "center", display: "flex", gap: 12 }}>
          <nav
            aria-label="Application navigation"
            className="pipeline-app-desktop-nav"
            style={{ alignItems: "center", display: "flex", gap: 24 }}
          >
            <a
              href={externalLinks.marketing}
              style={{
                color: "#4b5563",
                fontFamily: '"DM Sans", sans-serif',
                fontSize: 13,
                textDecoration: "none",
              }}
            >
              Learn about Pipeline
            </a>
          </nav>
          <button
            type="button"
            onClick={onSignIn}
            style={{
              background: "transparent",
              border: 0,
              color: "#1a3a5c",
              cursor: "pointer",
              fontFamily: '"DM Sans", sans-serif',
              fontSize: 13,
              fontWeight: 650,
              padding: 0,
            }}
          >
            Sign in
          </button>
          <button
            type="button"
            className="pipeline-app-mobile-toggle"
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileOpen((open) => !open)}
            style={{
              background: "transparent",
              border: "1px solid #d1d5db",
              borderRadius: 6,
              color: "#374151",
              cursor: "pointer",
              fontSize: 18,
              lineHeight: 1,
              padding: "7px 9px",
            }}
          >
            {mobileOpen ? "×" : "☰"}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          id="mobile-menu"
          style={{ background: "#fff", borderTop: "1px solid #e5e7eb" }}
        >
          <nav
            aria-label="Mobile navigation"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
              padding: "18px 24px 22px",
            }}
          >
            <a
              href={externalLinks.marketing}
              style={{
                color: "#374151",
                fontFamily: '"DM Sans", sans-serif',
                fontSize: 14,
              }}
            >
              Learn about Pipeline
            </a>
            <a
              href={externalLinks.ecosystem}
              style={{
                color: "#374151",
                fontFamily: '"DM Sans", sans-serif',
                fontSize: 14,
              }}
            >
              About 172X
            </a>
            <button
              type="button"
              onClick={() => {
                setMobileOpen(false);
                onSignIn();
              }}
              style={{
                alignSelf: "flex-start",
                background: "transparent",
                border: 0,
                color: "#1a3a5c",
                cursor: "pointer",
                fontFamily: '"DM Sans", sans-serif',
                fontSize: 14,
                fontWeight: 650,
                padding: 0,
              }}
            >
              Sign in
            </button>
            {ALPHA_ACCESS_ENABLED && (
              <button
                type="button"
                onClick={() => {
                  setMobileOpen(false);
                  onRequestAlpha();
                }}
                style={{
                  alignSelf: "flex-start",
                  background: "transparent",
                  border: 0,
                  color: "#1a3a5c",
                  cursor: "pointer",
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: 14,
                  fontWeight: 650,
                  padding: 0,
                }}
              >
                Request Alpha Access
              </button>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

export default function LandingPage({
  onRequestAlpha,
  onSignIn,
}: {
  onRequestAlpha: () => void;
  onSignIn: () => void;
}) {
  return (
    <div
      style={{
        background: "#f8fafc",
        color: "#111827",
        fontFamily: '"DM Sans", sans-serif',
        minHeight: "100vh",
      }}
    >
      <a
        href="#main-content"
        style={{
          left: 0,
          padding: "8px 16px",
          position: "absolute",
          top: -100,
          zIndex: 100,
        }}
        onFocus={(event) => (event.currentTarget.style.top = "0")}
        onBlur={(event) => (event.currentTarget.style.top = "-100px")}
      >
        Skip to main content
      </a>

      <PipelineHeader onRequestAlpha={onRequestAlpha} onSignIn={onSignIn} />

      <main id="main-content">
        <section
          aria-labelledby="pipeline-entry-title"
          style={{
            margin: "0 auto",
            maxWidth: 1120,
            padding: "clamp(72px, 12vw, 144px) 24px",
          }}
        >
          <div style={{ maxWidth: 680 }}>
            <p
              style={{
                color: "#64748b",
                fontFamily: "DM Mono, monospace",
                fontSize: 11,
                letterSpacing: "0.16em",
                margin: "0 0 20px",
                textTransform: "uppercase",
              }}
            >
              Private Alpha · Refactoring
            </p>
            <h1
              id="pipeline-entry-title"
              style={{
                color: "#0f172a",
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 300,
                fontSize: "clamp(36px, 6vw, 68px)",
                letterSpacing: "-0.045em",
                lineHeight: 1.02,
                margin: "0 0 24px",
                maxWidth: 700,
              }}
            >
              Build the record. Make the Ask.
            </h1>
            <p
              style={{
                color: "#475569",
                fontSize: "clamp(17px, 2vw, 21px)",
                lineHeight: 1.65,
                margin: "0 0 34px",
                maxWidth: 600,
              }}
            >
              Pipeline is the candidate-owned workspace for career evidence and
              deliberate decisions. It is currently in a private refactoring
              phase.
            </p>
            <p
              style={{
                color: "#475569",
                fontSize: 14,
                lineHeight: 1.6,
                marginBottom: 24,
              }}
            >
              New registration is paused. Existing users can continue to sign
              in.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              <ActionButton onClick={onSignIn}>
                Sign in to Pipeline
              </ActionButton>
              {ALPHA_ACCESS_ENABLED && (
                <ActionButton onClick={onRequestAlpha} variant="secondary">
                  Request Alpha Access
                </ActionButton>
              )}
            </div>
          </div>
        </section>

        <section
          aria-label="Pipeline application scope"
          style={{ background: "#0f2740", color: "#fff", padding: "56px 24px" }}
        >
          <div
            style={{
              display: "grid",
              gap: 28,
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              margin: "0 auto",
              maxWidth: 1120,
            }}
          >
            {[
              [
                "01",
                "Your evidence",
                "Keep your career evidence structured and under your control.",
              ],
              [
                "02",
                "Your ownership",
                "Your experience and boundaries remain yours.",
              ],
              [
                "03",
                "Your decisions",
                "Make deliberate career decisions grounded in your record.",
              ],
            ].map(([number, title, description]) => (
              <article key={number}>
                <p
                  style={{
                    color: "#93c5fd",
                    fontFamily: "DM Mono, monospace",
                    fontSize: 11,
                    letterSpacing: "0.14em",
                    margin: "0 0 16px",
                  }}
                >
                  {number}
                </p>
                <h2
                  style={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontSize: 20,
                    fontWeight: 300,
                    margin: "0 0 10px",
                  }}
                >
                  {title}
                </h2>
                <p style={{ color: "#cbd5e1", lineHeight: 1.6, margin: 0 }}>
                  {description}
                </p>
              </article>
            ))}
          </div>
        </section>
      </main>

      <footer
        style={{
          alignItems: "center",
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
          justifyContent: "space-between",
          margin: "0 auto",
          maxWidth: 1120,
          padding: "26px 24px 34px",
        }}
      >
        <p style={{ color: "#64748b", fontSize: 12, margin: 0 }}>
          {new Date().getFullYear()} {PRODUCT} · Developed by {COMPANY}
        </p>
        <a
          href={externalLinks.marketing}
          style={{ color: "#1a3a5c", fontSize: 12, textDecoration: "none" }}
        >
          Read the Pipeline story →
        </a>
      </footer>
    </div>
  );
}
